import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { backendApi } from '@/common/reducers/backend-api/backend-api';

interface TokenRevokerProps {
  initialTokenType?: 'facebook' | 'instagram';
}

export const TokenRevoker: React.FC<TokenRevokerProps> = ({ initialTokenType = 'facebook' }) => {
  const [isRevoking, setIsRevoking] = useState(false);
  const [tokenType, setTokenType] = useState<'facebook' | 'instagram'>(initialTokenType);
  const [isSpecificPermission, setIsSpecificPermission] = useState(false);
  const [permission, setPermission] = useState('');
  
  // Use the RTK Query mutation hook directly
  const [revokeTokenMutation] = backendApi.endpoints.revokeToken.useMutation();
  
  const handleRevokeToken = async () => {
    try {
      setIsRevoking(true);
      
      const params = {
        tokenType,
        ...(isSpecificPermission && permission ? { permission } : {})
      };
      
      // Call the RTK Query mutation
      const response = await revokeTokenMutation(params).unwrap();
      
      if (response.success) {
        toast.success('Token Revoked', {
          description: response.message
        });
      } else {
        toast.error('Failed to revoke token', {
          description: response.message || 'Unknown error'
        });
      }
    } catch (error) {
      console.error('Error revoking token:', error);
      toast.error('Error', {
        description: 'An unexpected error occurred while revoking the token.'
      });
    } finally {
      setIsRevoking(false);
    }
  };
  
  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Revoke OAuth Token</CardTitle>
        <CardDescription>
          Use this tool to test token revocation and refresh functionality
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="token-type">Token Type</Label>
          <Select
            value={tokenType}
            onValueChange={(value) => setTokenType(value as 'facebook' | 'instagram')}
          >
            <SelectTrigger id="token-type">
              <SelectValue placeholder="Select Token Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="facebook">Facebook</SelectItem>
              <SelectItem value="instagram">Instagram</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="specific-permission"
            checked={isSpecificPermission}
            onChange={(e) => setIsSpecificPermission(e.target.checked)}
            className="h-4 w-4"
          />
          <Label htmlFor="specific-permission">Revoke specific permission</Label>
        </div>
        
        {isSpecificPermission && (
          <div className="space-y-2">
            <Label htmlFor="permission">Permission Name</Label>
            <Input
              id="permission"
              placeholder="e.g. instagram_basic"
              value={permission}
              onChange={(e) => setPermission(e.target.value)}
            />
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button
          variant="destructive"
          onClick={handleRevokeToken}
          disabled={isRevoking}
          className="w-full"
        >
          {isRevoking ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Revoking Token...
            </>
          ) : (
            <>
              <XCircle className="mr-2 h-4 w-4" />
              Revoke Token
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default TokenRevoker; 